import { Plus, Trash2, ArrowUp, ArrowDown } from 'lucide-react'

import { newId } from './blocks'
import { FormDestinationEditor } from './FormDestinationEditor'
import { AlignPicker, OptionalNumber } from './blockEditors'
import { DraftTextField } from './appBlockEditors'
import { VisibilityEditor } from './VisibilityEditor'
import { ValidationsEditor } from './ValidationsEditor'
import type { FormBlock, FormFieldSpec, FormFieldType, FormSpamGuard, FormSuccess, PageInputOption } from './types'

interface Props {
  block: FormBlock
  onChange: (next: FormBlock) => void
  project?: string
  transactions?: { obj_name: string; title: string }[]
}

const TYPES: FormFieldType[] = [
  'text', 'email', 'tel', 'url', 'textarea', 'number', 'select', 'checkbox', 'radio',
  'date', 'time', 'file', 'rating', 'range', 'consent', 'hidden',
]

// Options are authored one per line as `label|value`. Split at the FIRST bar
// only, so a value may itself contain `|`; a line with no bar is its own value.
function optionsToText(opts: PageInputOption[] | undefined): string {
  return (opts ?? []).map(o => (
    o.value === o.label && !o.label.includes('|') ? o.label : `${o.label}|${o.value}`
  )).join('\n')
}
function textToOptions(text: string): PageInputOption[] {
  return text.split('\n').map(line => {
    const i = line.indexOf('|')
    const label = (i < 0 ? line : line.slice(0, i)).trim()
    const value = (i < 0 ? line : line.slice(i + 1)).trim()
    return { label, value: value || label }
  }).filter(o => o.label || o.value)
}

// Collapse an all-empty sub-object to undefined so clearing every control
// leaves no `{}` behind on the saved page.
function compact<T extends object>(o: T): T | undefined {
  return Object.values(o).some(v => v !== undefined) ? o : undefined
}

export function FormBlockEditor({ block, onChange, project = '', transactions = [] }: Props) {
  const set = (patch: Partial<FormBlock>) => onChange({ ...block, ...patch })
  const setSpam = (patch: Partial<FormSpamGuard>) => set({ spam: compact({ ...(block.spam || {}), ...patch }) })
  const setSuccess = (patch: Partial<FormSuccess>) => set({ success: compact({ ...(block.success || {}), ...patch }) })

  const updateField = (idx: number, patch: Partial<FormFieldSpec>) => {
    const fields = [...block.fields]
    fields[idx] = { ...fields[idx], ...patch }
    set({ fields })
  }
  const removeField = (idx: number) => set({ fields: block.fields.filter((_, i) => i !== idx) })
  const addField = () => set({
    fields: [
      ...block.fields,
      {
        id: newId('fld'),
        name: `field_${block.fields.length + 1}`,
        label: 'New field',
        type: 'text',
      } as FormFieldSpec,
    ],
  })
  const moveField = (idx: number, delta: -1 | 1) => {
    const next = [...block.fields]
    const to = idx + delta
    if (to < 0 || to >= next.length) return
    ;[next[idx], next[to]] = [next[to], next[idx]]
    set({ fields: next })
  }

  return (
    <div style={{ display: 'grid', gap: 12 }}>
      <Field label="Heading">
        <input type="text" value={block.heading ?? ''} onChange={e => set({ heading: e.target.value })} style={inputStyle} />
      </Field>
      <Field label="Subheading">
        <input type="text" value={block.subheading ?? ''} onChange={e => set({ subheading: e.target.value })} style={inputStyle} />
      </Field>

      <div style={{ borderTop: '1px solid var(--color-border)', paddingTop: 10, display: 'grid', gap: 8 }}>
        <header style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <strong style={{ fontSize: 13 }}>Fields ({block.fields.length})</strong>
          <button
            type="button"
            onClick={addField}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 4,
              padding: '4px 8px', fontSize: 13,
              border: '1px solid var(--color-border)', borderRadius: 4,
              background: 'var(--color-bg-secondary)', cursor: 'pointer',
            }}
          >
            <Plus size={12} /> Add field
          </button>
        </header>
        {block.fields.map((f, idx) => (
          <div
            key={f.id}
            style={{ border: '1px solid var(--color-border)', borderRadius: 6, padding: 10, display: 'grid', gap: 6 }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 6 }}>
              <input
                type="text"
                value={f.label}
                onChange={e => updateField(idx, { label: e.target.value })}
                style={{ ...inputStyle, fontWeight: 500 }}
              />
              <div style={{ display: 'flex', gap: 4 }}>
                <button type="button" onClick={() => moveField(idx, -1)} disabled={idx === 0} style={iconBtn}><ArrowUp size={12} /></button>
                <button type="button" onClick={() => moveField(idx, 1)}  disabled={idx === block.fields.length - 1} style={iconBtn}><ArrowDown size={12} /></button>
                <button type="button" onClick={() => removeField(idx)} style={iconBtn}><Trash2 size={12} /></button>
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
              <input
                type="text"
                value={f.name}
                onChange={e => updateField(idx, { name: e.target.value })}
                placeholder="name"
                style={inputStyle}
              />
              <select
                value={f.type}
                onChange={e => updateField(idx, { type: e.target.value as FormFieldType })}
                style={inputStyle}
              >
                {!TYPES.includes(f.type) && <option value={f.type}>{f.type} (unrecognised)</option>}
                {TYPES.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            {f.type !== 'hidden' && (
              <input
                type="text"
                value={f.placeholder ?? ''}
                onChange={e => updateField(idx, { placeholder: e.target.value || undefined })}
                placeholder={f.type === 'consent' ? 'Consent text (default: I agree)' : 'Placeholder'}
                style={inputStyle}
              />
            )}
            <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
              <input type="checkbox" checked={f.required ?? false} onChange={e => updateField(idx, { required: e.target.checked })} />
              Required
            </label>
            {(f.type === 'select' || f.type === 'radio') && (
              <Field label="Options (one per line, label|value)">
                <DraftTextField
                  multiline rows={3}
                  value={optionsToText(f.options)}
                  onCommit={text => {
                    const opts = textToOptions(text)
                    updateField(idx, { options: opts.length ? opts : undefined })
                  }}
                  placeholder={'Sales|sales\nSupport|support'}
                  style={{ ...inputStyle, fontFamily: 'inherit' }}
                />
              </Field>
            )}
            <FieldExtras field={f} onChange={patch => updateField(idx, patch)} />
          </div>
        ))}
      </div>

      <Field label="Submit button label">
        <input type="text" value={block.submitLabel} onChange={e => set({ submitLabel: e.target.value })} style={inputStyle} />
      </Field>
      <div style={{ borderTop: '1px solid var(--color-border)', paddingTop: 10 }}>
        <FormDestinationEditor
          project={project}
          transactions={transactions}
          block={block}
          fieldNames={block.fields.map((f) => f.name).filter(Boolean)}
          onChange={(patch) => set(patch)}
        />
      </div>
      <Field label="Layout">
        <select value={block.layout ?? 'stacked'} onChange={e => set({ layout: e.target.value as FormBlock['layout'] })} style={inputStyle}>
          {block.layout && block.layout !== 'stacked' && block.layout !== 'grid' && (
            <option value={block.layout}>{block.layout} (unrecognised)</option>
          )}
          <option value="stacked">Stacked</option>
          <option value="grid">Grid (2-up)</option>
        </select>
      </Field>
      <Field label="Alignment">
        <AlignPicker value={block.align ?? 'left'} onChange={align => set({ align })} />
      </Field>

      {/* What the visitor sees once the submission is accepted. */}
      <div style={{ borderTop: '1px solid var(--color-border)', paddingTop: 10, display: 'grid', gap: 8 }}>
        <strong style={{ fontSize: 13 }}>After submit</strong>
        <Field label="Go to URL instead (wins over the message)">
          <input
            type="text" value={block.success?.redirectUrl ?? ''} placeholder="/thank-you"
            onChange={e => setSuccess({ redirectUrl: e.target.value || undefined })}
            style={{ ...inputStyle, fontFamily: 'var(--font-mono, monospace)' }}
          />
        </Field>
        {!block.success?.redirectUrl && (
          <>
            <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
              <input
                type="checkbox" checked={block.success?.keepForm ?? false}
                onChange={e => setSuccess({ keepForm: e.target.checked || undefined })}
              />
              Keep the form on screen and clear it
            </label>
            {!block.success?.keepForm && (
              <>
                <Field label="Confirmation title">
                  <input
                    type="text" value={block.success?.title ?? ''} placeholder="Thanks!"
                    onChange={e => setSuccess({ title: e.target.value || undefined })}
                    style={inputStyle}
                  />
                </Field>
                <Field label="Confirmation message">
                  <textarea
                    rows={2} value={block.success?.message ?? ''} placeholder="Your message has been received."
                    onChange={e => setSuccess({ message: e.target.value || undefined })}
                    style={{ ...inputStyle, fontFamily: 'inherit', resize: 'vertical' }}
                  />
                </Field>
              </>
            )}
          </>
        )}
      </div>

      {/* Spam protection. Deliberately three controls and a sentence, not a
          panel: the check is AUTOMATIC once the project has keys, so the
          common case is an author who never opens this and is protected
          anyway. What is here is only how THIS form departs from that. */}
      <div style={{ borderTop: '1px solid var(--color-border)', paddingTop: 10, display: 'grid', gap: 8 }}>
        <strong style={{ fontSize: 13 }}>Spam protection</strong>
        <p style={{ margin: 0, fontSize: 13, color: 'var(--color-text-secondary)', lineHeight: 1.5 }}>
          A visitor who has not signed in is checked with Google reCAPTCHA v3 —
          scored in the background, nothing to solve — as soon as this project
          has a key in <strong>Advanced &rarr; Spam protection</strong>. The
          hidden-field and timing guards run either way.
        </p>
        <Field label="Run the check">
          <select
            value={block.spam?.captcha ?? 'auto'}
            onChange={e => setSpam({ captcha: e.target.value as FormSpamGuard['captcha'] })}
            style={inputStyle}
          >
            <option value="auto">When the visitor is not signed in (default)</option>
            <option value="always">Always, signed in or not</option>
            <option value="off">Never on this form</option>
          </select>
        </Field>
        {block.spam?.captcha !== 'off' && (
          <Field label="If the score is suspiciously low">
            <select
              value={block.spam?.captchaOnLowScore ?? ''}
              onChange={e => setSpam({
                captchaOnLowScore: (e.target.value || undefined) as FormSpamGuard['captchaOnLowScore'],
              })}
              style={inputStyle}
            >
              <option value="">Whatever the project decided</option>
              <option value="flag">Keep it and mark it in the inbox</option>
              <option value="reject">Refuse it</option>
            </select>
          </Field>
        )}
        {block.spam?.captcha !== 'off' && (
          <Field label="Lowest score that passes (0 – 1)">
            <OptionalNumber
              value={block.spam?.captchaMinScore} min={0} max={1} step={0.05}
              placeholder="Project threshold (0.5 unless changed)"
              onChange={n => setSpam({ captchaMinScore: n })}
            />
          </Field>
        )}
        {block.spam?.captchaOnLowScore === 'reject' && (
          <p style={{ margin: 0, fontSize: 13, color: 'var(--color-warning, #d97706)', lineHeight: 1.5 }}>
            A real customer whose browser scores badly is turned away with no
            trace of their message. Watch the scores in the form inbox before
            leaving this on.
          </p>
        )}
        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
          <input
            type="checkbox" checked={block.spam?.honeypot !== false}
            onChange={e => setSpam({ honeypot: e.target.checked ? undefined : false })}
          />
          Hidden-field (honeypot) guard
        </label>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
          {block.spam?.honeypot !== false && (
            <Field label="Hidden field name">
              <input
                type="text" value={block.spam?.honeypotName ?? ''} placeholder="_pg_hp"
                onChange={e => setSpam({ honeypotName: e.target.value || undefined })}
                style={{ ...inputStyle, fontFamily: 'var(--font-mono, monospace)' }}
              />
            </Field>
          )}
          <Field label="Reject if filled in under (s)">
            <OptionalNumber
              value={block.spam?.minSeconds} min={0} step={1} placeholder="2"
              onChange={n => setSpam({ minSeconds: n })}
            />
          </Field>
        </div>
      </div>
    </div>
  )
}

// Per-field settings beyond name/type/label: the starting value, help line,
// bounds for rating/range, the validation rules (the same editor a page
// variable uses) and the conditional-visibility rule.
function FieldExtras({ field: f, onChange }: { field: FormFieldSpec; onChange: (patch: Partial<FormFieldSpec>) => void }) {
  const rules = f.validations ?? []
  return (
    <>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
        <Field label={f.type === 'hidden' ? 'Value' : 'Default value'}>
          <input
            type="text" value={f.defaultValue ?? ''} placeholder={f.type === 'hidden' ? 'supports {{vars}}' : ''}
            onChange={e => onChange({ defaultValue: e.target.value || undefined })}
            style={inputStyle}
          />
        </Field>
        {f.type !== 'hidden' && (
          <Field label={f.type === 'consent' ? 'Help (used as text if no placeholder)' : 'Help text'}>
            <input
              type="text" value={f.helpText ?? ''}
              onChange={e => onChange({ helpText: e.target.value || undefined })}
              style={inputStyle}
            />
          </Field>
        )}
      </div>
      {f.type === 'rating' && (
        <Field label="Stars">
          <OptionalNumber value={f.max} min={1} max={10} step={1} placeholder="5" onChange={n => onChange({ max: n })} />
        </Field>
      )}
      {f.type === 'range' && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 6 }}>
          <Field label="Min"><OptionalNumber value={f.min} placeholder="0" onChange={n => onChange({ min: n })} /></Field>
          <Field label="Max"><OptionalNumber value={f.max} placeholder="100" onChange={n => onChange({ max: n })} /></Field>
          <Field label="Step"><OptionalNumber value={f.step} step="any" placeholder="1" onChange={n => onChange({ step: n })} /></Field>
        </div>
      )}
      <ValidationsEditor rules={rules} onChange={(next) => onChange({ validations: next.length ? next : undefined })} />
      <details>
        <summary style={{ fontSize: 13, color: 'var(--color-text-secondary)', cursor: 'pointer' }}>
          Show this field only when…{f.visibility ? ' (set)' : ''}
        </summary>
        <div style={{ paddingTop: 6 }}>
          <VisibilityEditor value={f.visibility} onChange={visibility => onChange({ visibility })} />
          <span style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>
            On a field, roles, schedule and condition apply; device toggles do not.
          </span>
        </div>
      </details>
    </>
  )
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ display: 'grid', gap: 4 }}>
      <span style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>{label}</span>
      {children}
    </div>
  )
}
const inputStyle: React.CSSProperties = {
  width: '100%', padding: '6px 8px', fontSize: 13,
  border: '1px solid var(--color-border-strong)', borderRadius: 4,
  background: 'var(--color-input-bg)', color: 'var(--color-text)',
}
const iconBtn: React.CSSProperties = {
  background: 'var(--color-bg-secondary)', border: '1px solid var(--color-border)',
  borderRadius: 4, padding: 4, cursor: 'pointer', color: 'var(--color-text-secondary)',
}
